import { Button, Modal, TextField, useToast } from '@citizen-walk/shared-ui';
import { httpsCallable } from 'firebase/functions';
import { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { functions } from '../../lib/firebase';
import { useAdminAuth } from '../auth';
import type { StreetSurveyPost } from './useStreetSurveyPosts';
import type { PrimaryAxisResolution } from './streetSurveyAxisTypes';
import type { RoleTypeAxisResolution } from './streetSurveyAxisTypes';
import {
  AXIS_GRANULARITY_LABELS,
  StreetSurveyAggregationPanel,
  toPrimaryAxis,
  type AggregationSelection,
} from './StreetSurveyAggregationPanel';
import { computeAxisMatrix } from './computeAxisMatrix';
import type { ResolvedSurveyAxis } from './useStreetSurveyConfig';
import { resolveMapLegendAxis, resolvePostCategoryCounts } from './streetSurveyPinDisplay';
import { resolveDonutSegments } from './streetSurveyClusterDonut';
import { AreaReportPreviewMap, type AreaReportPreviewMapHandle } from './AreaReportPreviewMap';
import { AreaReportPdfTemplate } from './AreaReportPdfTemplate';
import { generateAreaReportPdf } from './generateAreaReportPdf';
import { saveAreaReportSnapshot } from './areaReportSnapshotActions';
import {
  computeAxisCounts,
  computeEvaluationSummary,
  expandAxisCountsByPolarity,
} from './computeAreaAxisCounts';
import type { EvaluationAxisDef } from '@citizen-walk/shared-types';

interface GenerateAreaSummaryResult {
  ok: boolean;
  summary?: string;
  error?: string;
}

export interface AreaReportModalProps {
  districtId: string;
  /** 集計している調査 (docs/98 R-97)。保存と詳細レポートのリンクに使う。 */
  surveyKey?: string;
  /** 詳細レポートのリンクに付ける検索文字列 (例 `?survey=bousai`)。 */
  detailQuery?: string;
  postIds: string[];
  posts: StreetSurveyPost[];
  primaryAxis: PrimaryAxisResolution;
  roleTypeAxis?: RoleTypeAxisResolution;
  /** エリア選択に使った多角形 (`StreetSurveyResultsMap` の onLassoSelect 経由)。 */
  polygon?: { latitude: number; longitude: number }[] | null;
  onClose: () => void;
  /** docs/27 §9: 評価軸 (無ければ内訳なし) と集計に使った調査項目の key。 */
  evaluation?: EvaluationAxisDef | null;
  axisKey?: string | null;
  /** 選べる調査項目 (この画面で集計項目・マトリクスの軸を切り替えるため、docs/27 §9-3)。 */
  surveyAxes?: ResolvedSurveyAxis[];
}

export function AreaReportModal({
  districtId,
  postIds,
  posts,
  primaryAxis,
  roleTypeAxis,
  polygon = null,
  onClose,
  surveyKey,
  detailQuery = '',
  evaluation = null,
  axisKey = null,
  surveyAxes = [],
}: AreaReportModalProps) {
  const { toast } = useToast();
  const { state: authState } = useAdminAuth();
  const [summary, setSummary] = useState<string | null>(null);
  const [summarizing, setSummarizing] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [savingName, setSavingName] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const previewMapRef = useRef<AreaReportPreviewMapHandle>(null);
  const pdfTemplateRef = useRef<HTMLDivElement>(null);
  const selected = posts.filter((p) => postIds.includes(p.id));

  // description が無い地点 (解析未完了・失敗) は要約対象から除外する。
  const summarizablePosts = selected.filter((p) => p.description);

  async function handleGenerateSummary() {
    if (summarizablePosts.length === 0 || summarizing) return;
    setSummarizing(true);
    setSummary(null);
    try {
      const pointTexts = summarizablePosts.map(
        (p) => `${p.description}${p.professionalInsight ? ` (${p.professionalInsight})` : ''}`,
      );
      const callable = httpsCallable<
        { districtId: string; pointTexts: string[] },
        GenerateAreaSummaryResult
      >(functions, 'generateAreaSummary');
      const res = await callable({ districtId, pointTexts });
      if (res.data.ok && res.data.summary) {
        setSummary(res.data.summary);
      } else {
        toast('error', `エリア要約の生成に失敗しました: ${res.data.error ?? '不明なエラー'}`);
      }
    } catch (e) {
      toast('error', `エリア要約の生成に失敗しました: ${(e as Error).message}`);
    } finally {
      setSummarizing(false);
    }
  }

  async function handleExportPdf() {
    if (exportingPdf) return;
    setExportingPdf(true);
    try {
      const mapDataUrl = await previewMapRef.current?.capture();
      if (!pdfTemplateRef.current) return;
      await generateAreaReportPdf({
        templateRef: pdfTemplateRef.current,
        fileName: `エリア選択集計_${selected.length}地点.pdf`,
        mapDataUrl,
      });
    } catch (e) {
      toast('error', `PDF生成に失敗しました: ${(e as Error).message}`);
    } finally {
      setExportingPdf(false);
    }
  }

  async function handleSaveSnapshot() {
    const name = savingName?.trim();
    if (!name || !authState.user || saving) return;
    setSaving(true);
    try {
      await saveAreaReportSnapshot(
        {
          districtId,
          name,
          polygon: polygon ?? [],
          postIds: selected.map((p) => p.id),
          pointCount: selected.length,
          axisCounts: axes,
          axisKey: agg.rowAxisKey ?? axisKey,
          evaluation: evaluationSummary,
          summary,
          surveyKey,
        },
        authState.user.uid,
      );
      toast('success', `「${name}」として保存しました。`);
      setSavingName(null);
    } catch (e) {
      toast('error', `保存に失敗しました: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  // 集計する調査項目・細かさ・表示はこの画面で切り替えられる (保存・PDF も同じ内容になる)。
  const [agg, setAgg] = useState<AggregationSelection>({
    rowAxisKey: axisKey,
    colAxisKey: null,
    granularity: 'category',
    view: 'bar',
    splitByPolarity: false,
  });
  const aggAxis = surveyAxes.find((a) => a.key === agg.rowAxisKey) ?? null;
  const aggColAxis = surveyAxes.find((a) => a.key === agg.colAxisKey) ?? null;
  const axes = useMemo(
    () =>
      computeAxisCounts(
        selected,
        aggAxis ? toPrimaryAxis(aggAxis) : primaryAxis,
        evaluation,
        agg.granularity,
      ),
    // selected は postIds/posts から毎回作られるため、その元で判定する。
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [posts, postIds, aggAxis, primaryAxis, evaluation, agg.granularity],
  );
  const matrix = useMemo(
    () => computeAxisMatrix(selected, aggAxis, aggColAxis, evaluation, agg.granularity),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [posts, postIds, aggAxis, aggColAxis, evaluation, agg.granularity],
  );
  const evaluationSummary = computeEvaluationSummary(selected, evaluation);
  // PDF は画面と同じ内容にする (評価で分ける表示なら、PDF の棒も項目 × 評価に展開する)。
  // 軽い変換なので memo 化しない (axes 自体が memo 済み)。
  const pdfAxes = agg.splitByPolarity
    ? expandAxisCountsByPolarity(axes, evaluation?.polarityLabels)
    : axes;

  // 結果地図と同じ軸解決 (role_type があれば優先、無ければ primary 軸) でドーナツを描く
  // (2026-07-16、地図上の地点表示をドット→ドーナツ表示に変更)。
  const legendAxis = resolveMapLegendAxis(primaryAxis, roleTypeAxis);
  const previewMapPoints = selected.map((p, i) => ({
    id: p.id,
    lat: p.location.latitude,
    lng: p.location.longitude,
    number: i + 1,
    segments: legendAxis
      ? resolveDonutSegments(resolvePostCategoryCounts(p, legendAxis.axis), legendAxis.axis)
      : [],
  }));

  return (
    <Modal open onClose={onClose} title={`エリア選択集計（${selected.length}地点）`} size="xl">
      <div className="flex items-center justify-end mb-4">
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            disabled={savingName !== null}
            onClick={() => setSavingName('')}
          >
            エリアとして保存
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={exportingPdf}
            onClick={() => void handleExportPdf()}
          >
            {exportingPdf ? 'PDF生成中…' : 'PDFでダウンロード'}
          </Button>
        </div>
      </div>

      {/* エリア比較ダッシュボード (docs/25 §10-5) 用の名前付き保存フォーム。保存後もモーダルは
          開いたままにする (連続して別名で保存できるようにするため)。 */}
      {savingName !== null && (
        <div className="mb-4 flex items-end gap-2 bg-slate-50 rounded px-3 py-2">
          <TextField
            label="エリア名"
            value={savingName}
            onChange={setSavingName}
            placeholder="例: 駅前広場"
            className="flex-1"
          />
          <Button
            size="sm"
            disabled={!savingName.trim() || saving}
            onClick={() => void handleSaveSnapshot()}
          >
            {saving ? '保存中…' : '保存する'}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={saving}
            onClick={() => setSavingName(null)}
          >
            キャンセル
          </Button>
        </div>
      )}

      <AreaReportPreviewMap
        ref={previewMapRef}
        points={previewMapPoints}
        polygon={polygon}
        className="w-full h-56 rounded border border-slate-200 mb-4"
      />

      {/* PDF出力用の隠しテンプレート (画面には表示しない、html2canvasキャプチャ専用)。
          `Modal` の中身ラッパーは `max-h-[90vh] overflow-auto` を持つため、その内側にネストして
          置くとテンプレートの実際の高さがモーダルの表示可能領域より長い場合に html2canvas の
          キャプチャが途中で切れてしまう不具合が判明した (2026-07-16、実運用で発覚、html2canvasの
          既知の制約: overflow:auto/hidden な祖先の内側にある要素は正しくキャプチャできないことが
          ある)。`createPortal` で `document.body` 直下に描画し、モーダルの overflow 制約を完全に
          回避する。 */}
      {createPortal(
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '794px',
            opacity: 0,
            zIndex: -9999,
            pointerEvents: 'none',
          }}
        >
          <AreaReportPdfTemplate
            ref={pdfTemplateRef}
            pointCount={selected.length}
            summary={summary}
            axes={pdfAxes}
            posts={selected}
            generatedAt={new Date().toLocaleString('ja-JP')}
            axisLabel={aggAxis?.label ?? primaryAxis.primaryAxis?.label ?? '調査項目'}
            granularityLabel={
              agg.granularity === 'auto' ? undefined : AXIS_GRANULARITY_LABELS[agg.granularity]
            }
            view={agg.view}
            matrix={matrix}
            matrixRowLabel={aggAxis?.label ?? ''}
            matrixColLabel={aggColAxis?.label ?? ''}
          />
        </div>,
        document.body,
      )}

      <div className="mb-4 pb-4 border-b border-slate-200">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-bold text-slate-700">エリアの特徴</h3>
          <Button
            size="sm"
            variant="secondary"
            disabled={summarizablePosts.length === 0 || summarizing}
            onClick={() => void handleGenerateSummary()}
          >
            {summarizing ? '生成中…' : summary ? '要約を再生成' : '要約を生成'}
          </Button>
        </div>
        {summarizablePosts.length === 0 ? (
          <p className="text-xs text-slate-500">解析済みの地点がないため、要約を生成できません。</p>
        ) : summary ? (
          <p className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 rounded px-3 py-2">
            {summary}
          </p>
        ) : (
          <p className="text-xs text-slate-500">
            選択した{summarizablePosts.length}
            地点の分析結果から、AIがエリア全体の特徴を3行程度で要約します。
          </p>
        )}
      </div>

      <div>
        <h3 className="text-sm font-bold text-slate-700 mb-2">検出要素の集計</h3>
        <StreetSurveyAggregationPanel
          posts={selected}
          axes={surveyAxes}
          evaluation={evaluation}
          initialRowAxisKey={agg.rowAxisKey}
          onChange={setAgg}
        />
      </div>

      <h3 className="text-sm font-bold text-slate-700 mt-5 mb-2">解析対象となった地点一覧</h3>
      <ul className="grid grid-cols-1 gap-2 max-h-60 overflow-auto">
        {selected.map((p, i) => (
          <li
            key={p.id}
            className="flex items-center justify-between gap-2 rounded border border-slate-200 px-3 py-2"
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-xs text-slate-400 shrink-0">{i + 1}.</span>
              <span className="text-xs text-slate-600 truncate">{p.description}</span>
            </div>
            <Link
              to={`/districts/${districtId}/street-survey/${p.id}${detailQuery}`}
              className="text-xs text-primary-600 hover:underline shrink-0"
            >
              詳細を見る →
            </Link>
          </li>
        ))}
      </ul>
    </Modal>
  );
}
